import React from 'react';

import { Button, Divider, Drawer, Typography } from '@material-ui/core';
import ButtonBase from '@material-ui/core/ButtonBase';
import { useQuery } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';

import usersApi from 'api/rest/users/users';
import exercisesImageSrc from 'assets/icons/dashboard/exercises.png';
import helpImageSrc from 'assets/icons/dashboard/help.png';
import homeImageSrc from 'assets/icons/dashboard/home.png';
import ongoImageSrc from 'assets/icons/dashboard/ongo.png';
import setsImageSrc from 'assets/icons/dashboard/sets.png';
import settingsImageSrc from 'assets/icons/dashboard/settings.png';
import sheetsImageSrc from 'assets/icons/dashboard/sheets.png';
import subjectImageSrc from 'assets/icons/dashboard/subject.png';
import teacherImageSrc from 'assets/icons/dashboard/teacher.png';
import { ReactComponent as AvatarIcon } from 'assets/icons/general/avatar.svg';
import DrawerWrapper from 'components/DrawerWrapper/DrawerWrapper';
import MenuLink, { DisabledReason, MenuLinkType } from 'components/MenuLink/MenuLink';
import PATHS, { USER_SETTINGS_DIALOGS } from 'constants/paths';
import QUERY from 'constants/queryKeys';
import { useAuthContext } from 'context/AuthContext';
import { useUserLogContext } from 'context/UserLogContext/UserLogContext';
import useOnGoAvailability from 'hooks/useOnGoAvailability/useOnGoAvailability';
import useLayoutStore from 'storages/layout';
import trimString from 'utils/trimString/trimString';

import useStyles from './MenuDrawer.styles';

const getOnGoMenuDisabledReason = (isOnGoEnabled: boolean, isOnGoUnavailableForSubject: boolean): DisabledReason | undefined => {
  if (!isOnGoEnabled) {
    return 'under_construction';
  }

  if (isOnGoUnavailableForSubject) {
    return 'unavailable_for_subject';
  }

  return undefined;
};

const getMenuLinks = ({
  isPremiumUser,
  isTeacher,
  isOnGoEnabled,
  isOnGoUnavailableForSubject,
}: {
  isPremiumUser?: boolean;
  isTeacher: boolean;
  isOnGoEnabled: boolean;
  isOnGoUnavailableForSubject: boolean;
}): (MenuLinkType & {
  showDivider?: boolean;
})[] => [
  ...(isTeacher
    ? [
        {
          label: 'Panel nauczyciela',
          to: PATHS.TEACHER_DASHBOARD,
          icon: teacherImageSrc,
          showDivider: true,
          disabled: isPremiumUser ? undefined : ('premium' as DisabledReason),
        },
      ]
    : []),
  { label: 'Wybierz przedmiot', to: PATHS.SUBJECT_SELECT, icon: subjectImageSrc },
  { label: 'Menu główne', to: PATHS.ROOT, icon: homeImageSrc },
  { label: 'Przeglądaj zadania', to: PATHS.EXERCISES, icon: exercisesImageSrc },
  { label: 'Rozwiązuj arkusze', to: PATHS.SHEETS, icon: sheetsImageSrc, disabled: isPremiumUser ? undefined : 'premium' },
  {
    label: 'Twoje zestawy zadań',
    to: PATHS.EXERCISES_SETS,
    icon: setsImageSrc,
    disabled: isPremiumUser ? undefined : 'premium',
    showDivider: isTeacher ? true : false,
  },
  ...(!isTeacher
    ? [
        {
          label: 'Rozwiązuj w głowie',
          to: PATHS.ON_GO,
          icon: ongoImageSrc,
          disabled: getOnGoMenuDisabledReason(isOnGoEnabled, isOnGoUnavailableForSubject),
          showDivider: true,
        },
      ]
    : []),
  {
    label: 'Ustawienia',
    to: PATHS.USER_SETTINGS,
    icon: settingsImageSrc,
  },

  {
    label: 'Pomoc',
    to: PATHS.HELP,
    icon: helpImageSrc,
  },
];

type Props = {
  isOpen: boolean;
  onClose: () => void;
};

const MenuDrawer: React.FC<Props> = ({ isOpen, onClose }) => {
  const { hasOnGoAccess: isOnGoEnabled, isOnGoUnavailableForSubject } = useOnGoAvailability();
  const { logEvent } = useUserLogContext();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { username, onLogout, isTeacher } = useAuthContext();
  const { toggleIsMenuVisible, menuIndicator } = useLayoutStore();
  const { data } = useQuery(QUERY.GET_ME, usersApi.getMe());

  const logout = () => {
    logEvent('log-out');
    toggleIsMenuVisible();
    onLogout();
  };

  const onBuyClick = () => {
    logEvent('buy-click');
  };

  const classes = useStyles();
  return (
    <Drawer open={isOpen} onClose={onClose} anchor='right'>
      <DrawerWrapper onClose={onClose} className={classes.wrapper}>
        <div className={classes.header}>
          <AvatarIcon className={classes.avatarIcon} />
          <div>
            <Typography variant='subtitle1'>{trimString(22, username)}</Typography>
            <ButtonBase disableRipple onClick={logout}>
              <Typography variant='subtitle2'>Wyloguj się</Typography>
            </ButtonBase>
          </div>
        </div>
        <Divider className={classes.divider} />
        {getMenuLinks({
          isPremiumUser: data?.hasPremiumAccess,
          isTeacher,
          isOnGoEnabled,
          isOnGoUnavailableForSubject,
        }).map(menuLinkProp => {
          const { showDivider, ...props } = menuLinkProp;
          return (
            <span key={menuLinkProp.to}>
              <MenuLink showIndicator={menuIndicator === props.to} key={menuLinkProp.to} {...props} />
              {showDivider && <Divider className={classes.divider} />}
            </span>
          );
        })}
        {data && !data.hasPremiumAccess && !data.isSchoolAccount && (
          <Button
            onClick={onBuyClick}
            className={classes.purchaseButton}
            size='large'
            color='secondary'
            variant='contained'
            component={RouterLink}
            to={`${PATHS.USER_SETTINGS}/${USER_SETTINGS_DIALOGS.SUBSCRIBE}`}
          >
            Kup plan premium
          </Button>
        )}
      </DrawerWrapper>
    </Drawer>
  );
};

export default MenuDrawer;
